/**
 * Helpers for the 429 responses the credential endpoints return.
 *
 * The API sends `retry_after` (seconds) in the body and a standard `Retry-After`
 * header. Without one of those the user can only be told to try again "later",
 * which is what CL-17932 asked to fix, so read whichever is present and fall
 * back to a minute rather than showing nothing.
 */
const FALLBACK_SECONDS = 60;

/**
 * Pull the wait, in whole seconds, out of an axios error or a fetch Response.
 * Returns 0 when the response is not a throttle response.
 */
export function retryAfterSeconds(source) {
  const response = source && source.response ? source.response : source;

  if (!response || response.status !== 429) return 0;

  const body = response.data || {};
  const fromBody = Number(body.retry_after);
  if (Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);

  const header = response.headers
    ? // axios exposes a plain object, fetch a Headers instance
      (typeof response.headers.get === "function"
        ? response.headers.get("retry-after")
        : response.headers["retry-after"])
    : null;

  const fromHeader = Number(header);
  if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.ceil(fromHeader);

  return FALLBACK_SECONDS;
}

/**
 * "45 seconds" / "2:05" style wait, for use inside a sentence.
 */
export function formatRetryAfter(seconds) {
  const total = Math.max(0, Math.ceil(Number(seconds) || 0));

  if (total < 60) return `${total}s`;

  const minutes = Math.floor(total / 60);
  const remainder = total % 60;

  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}
